import { defineStore } from 'pinia'
import { reactive } from 'vue'
import { type INewsCategoryModel, type INewsModel } from '../types/news'

export type ENewsPageType = 'post' | 'pagination' | 'category'

export interface INewsPageContext {
	type: ENewsPageType
	page: number
	post: INewsModel | null
	category: INewsCategoryModel | null
	categorySlug: string | null
}

export interface INewsStoreState {
	latestNews: INewsModel[]
	lastNews: INewsModel[]
	pageContext: INewsPageContext | null
}

export interface INewsStore {
	readonly state: {
		latestNews: INewsModel[]
		lastNews: INewsModel[]
		pageContext: INewsPageContext | null
	}

	setLatestNews(news: INewsModel[]): void
	fetchLastNews(): Promise<INewsModel[]>
	getLatestNews(): Promise<INewsModel[]>
	setNewsPageContext(context: Partial<INewsPageContext> & { type: ENewsPageType }): void
}

export const useNewsStore = defineStore('newsStore', (): INewsStore => {
	const { getLocaleLang } = usei18nService()

	const _state = reactive<INewsStoreState>({
		latestNews: [],
		lastNews: [],
		pageContext: null,
	})

	let lastNewsLang: string | null = null
	let latestNewsLang: string | null = null

	const setLatestNews = (news: INewsModel[]) => {
		_state.latestNews = news
	}

	const setNewsPageContext = (context: Partial<INewsPageContext> & { type: ENewsPageType }) => {
		_state.pageContext = { page: 1, post: null, category: null, categorySlug: null, ...context }
	}

	const fetchLastNews = async () => {
		const currentLang = getLocaleLang()
		// Cache per język — pusty wynik też jest zapamiętany (nie refetchujemy w kółko).
		if (lastNewsLang === currentLang) {
			return _state.lastNews
		}
		try {
			const { data } = await ApiService.fetchNews(1, 10, '', currentLang)
			_state.lastNews = data ?? []
			lastNewsLang = currentLang
		} catch (error) {
			logger.log(error)
		}
		return _state.lastNews
	}

	const getLatestNews = async () => {
		const currentLang = getLocaleLang()
		if (latestNewsLang === currentLang) {
			return _state.latestNews
		}
		try {
			const { data } = await ApiService.fetchNews(1, 4, '', currentLang)
			setLatestNews(data ?? [])
			latestNewsLang = currentLang
		} catch (error) {
			logger.log(error)
		}
		return _state.latestNews
	}

	return {
		state: _state,
		fetchLastNews,
		setLatestNews,
		getLatestNews,
		setNewsPageContext,
	}
})
